/**
 * Utility for creating stable, parseable item keys that avoid the fragile
 * "composite ID in a string" pattern with dash concatenation.
 *
 * Uses '::' as separator since it never appears in item IDs or tracking numbers.
 */

export type ItemIdentity = {
  id: string;
  trackingNumber?: string | null;
};

const SEPARATOR = '::';

/**
 * Creates a stable key from an item identity.
 * Uses trackingNumber when available to distinguish items across shipments.
 */
export const makeItemKey = ({ id, trackingNumber }: ItemIdentity): string => {
  return trackingNumber ? `${id}${SEPARATOR}${trackingNumber}` : id;
};

/**
 * Parses a key back into its component parts.
 */
export const parseItemKey = (key: string): ItemIdentity => {
  const separatorIndex = key.indexOf(SEPARATOR);

  if (separatorIndex === -1) {
    return { id: key, trackingNumber: null };
  }

  const id = key.substring(0, separatorIndex);
  const trackingNumber = key.substring(separatorIndex + SEPARATOR.length);

  return { id, trackingNumber };
};

/**
 * Extracts the base item ID from a key (without tracking number).
 */
export const getBaseItemId = (key: string): string => {
  return parseItemKey(key).id;
};
